/**
 * extractErrorMessage — reduce any error shape returned from the API or
 * thrown by axios down to a single human-readable string that's safe to
 * pass to toast.error(), inline form messages, or alert UI.
 *
 * Background (2026-06-25): the Confidential tab crashed the page with
 *   "Objects are not valid as a React child (found: object with keys
 *    {type, loc, msg, input, url})"
 * because FastAPI returns 422 validation errors as
 *   { detail: [ { type, loc, msg, input, url, ctx? }, ... ] }
 * and `toast.error(err.response.data.detail)` was passing the array of
 * objects straight to React. React tried to render them → boundary fired.
 *
 * Handles:
 *  • err.response.data.detail as a string  → returns the string
 *  • err.response.data.detail as a Pydantic 422 array → joins each entry's
 *    "<field>: <msg>" so a user sees something like
 *    "first_name: Field required; email: value is not a valid email address"
 *  • err.response.data.detail as a single object with `msg`/`message`
 *  • err.response.data.message — plain string
 *  • Network error (no response) → err.message
 *  • Unknown → fallback string
 *
 * The shape only depends on what FastAPI returns, so a single helper covers
 * every form in the app and protects future ones automatically.
 */
function fieldLabelFromLoc(loc) {
  // loc looks like ["body", "first_name"] or ["body", "addresses", 0, "zip"].
  // Drop the leading "body" / "query" and join the rest with dots for clarity.
  if (!Array.isArray(loc) || loc.length === 0) return null;
  const tail = loc[0] === 'body' || loc[0] === 'query' || loc[0] === 'path'
    ? loc.slice(1)
    : loc;
  return tail.length ? tail.join('.') : null;
}

export function extractErrorMessage(err, fallback = 'Request failed') {
  if (!err) return fallback;
  if (typeof err === 'string') return err;

  const data = err.response?.data;

  if (data) {
    // FastAPI 422 — { detail: [ ... ] }
    if (Array.isArray(data.detail)) {
      const parts = data.detail.map((d) => {
        if (typeof d === 'string') return d;
        const field = fieldLabelFromLoc(d.loc);
        const msg = d.msg || d.message || 'invalid value';
        return field ? `${field}: ${msg}` : msg;
      }).filter(Boolean);
      if (parts.length) return parts.slice(0, 5).join('; ');
    }

    // FastAPI HTTPException — { detail: "string" } (most common 4xx case)
    if (typeof data.detail === 'string') return data.detail;

    // Some endpoints return { detail: { msg: ... } } or { message: ... }
    if (data.detail && typeof data.detail === 'object') {
      if (typeof data.detail.msg === 'string') return data.detail.msg;
      if (typeof data.detail.message === 'string') return data.detail.message;
    }
    if (typeof data.message === 'string') return data.message;
  }

  if (typeof err.message === 'string' && err.message) return err.message;
  return fallback;
}

export default extractErrorMessage;
